"use client";
import { useTranslations } from "next-intl"

import { useCallback, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ListingResults } from "@/components/search/search-components/results/listing-results";
import { PackageOpen } from "lucide-react";
import { ProfileCollectionSlider } from "@/components/profile-page/profile-collection-slider";
import {
  ProfileCollectionHighlights,
  type ProfileCollectionHighlight,
} from "@/components/profile-page/profile-collection-highlights";
import { ProfileTabBar } from "@/components/profile-page/profile-tab-bar";
import { AddItemsToCollectionDialog } from "@/components/my-listings/add-items-to-collection-dialog";
import { sortProfileListings } from "@/lib/profile-listings-sort";
import type { ListingRow } from "@/types/listing";

interface ListingResult {
  id: string;
  title: string;
  description: string;
  price: string;
  currency?: string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: structured trio replaces
  // free-text `location`. `null` for orphan listings. Display chip lives
  // at the profile-hero level (reads `seller.country`).
  shipFromAddress?: { city: string | null; state: string | null; country: string | null } | null;
  image_urls: string[];
  created_at: string;
  condition: string;
  grade: string;
  quantity: number;
  username?: string;
  avatar_url?: string | null;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  type?: "standard" | "preorder" | null;
  user_id?: string;
  // Phase 3 (2026-06-29): `accepts_offers` collapsed — derive via
  // `isOfferEnabledFormat(listing_format)` from `@kraahl/contract`.
  offers_pending_count?: number | null;
}

interface Collection {
  id: string;
  title: string;
  description?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  display_order: number;
  listings: ListingResult[];
}

interface ProfileTabsProps {
  listings: ListingResult[];
  collections?: Collection[];
  currentUserId?: string;
  profileUsername?: string;
  isOwner?: boolean;
  sellerBanned?: boolean;
  onCollectionTitleChange?: (collectionId: string, newTitle: string) => Promise<void>;
  onCollectionDescriptionChange?: (collectionId: string, newDescription: string) => Promise<void>;
  onListingRemovedFromCollection?: (collectionId: string, listingId: string) => void;
  onCollectionCreated?: (collection: ProfileCollectionHighlight) => void;
  onCollectionDeleted?: (collectionId: string) => void | Promise<void>;
  onAddItemsToCollection?: (collectionId: string, listingIds: string[]) => Promise<boolean>;
  hasMoreListings?: boolean;
  onLoadMoreListings?: () => void | Promise<void>;
  isLoadingMoreListings?: boolean;
  searchActive?: boolean;
  totalListingsCount?: number;
  tabsRef?: React.RefObject<HTMLElement | null>;
  mobileHeaderTabsOpacity?: number;
}

export function ProfileTabs({
  listings,
  collections = [],
  currentUserId,
  profileUsername,
  isOwner = false,
  sellerBanned = false,
  onCollectionTitleChange,
  onCollectionDescriptionChange,
  onListingRemovedFromCollection,
  onCollectionCreated,
  onCollectionDeleted,
  onAddItemsToCollection,
  hasMoreListings,
  onLoadMoreListings,
  isLoadingMoreListings = false,
  searchActive = false,
  totalListingsCount,
  tabsRef,
  mobileHeaderTabsOpacity = 0,
}: ProfileTabsProps) {
  const t = useTranslations("profile.tabs");
  const tProfile = useTranslations("profile");
  const router = useRouter();
  const internalTabsRef = useRef<HTMLDivElement>(null);
  const resolvedTabsRef = tabsRef ?? internalTabsRef;
  const [collectionBrowseOpen, setCollectionBrowseOpen] = useState(false);
  const [collectionBrowseId, setCollectionBrowseId] = useState<string | null>(null);
  const [addItemsOpen, setAddItemsOpen] = useState(false);
  const [isAddingItems, setIsAddingItems] = useState(false);

  const sortedCollections = useMemo(
    () =>
      collections.map((collection) => ({
        ...collection,
        listings: sortProfileListings([...collection.listings]),
      })),
    [collections],
  );

  const collectionsWithItems = useMemo(
    () => sortedCollections.filter((collection) => collection.listings.length > 0),
    [sortedCollections],
  );

  const displayCollections = isOwner ? sortedCollections : collectionsWithItems;

  const highlightCollections = useMemo(
    () =>
      displayCollections.map((collection) => ({
        id: collection.id,
        title: collection.title,
        listings: collection.listings,
      })),
    [displayCollections],
  );

  const browseCollection = useMemo(() => {
    if (!collectionBrowseId) return null;
    return displayCollections.find((c) => c.id === collectionBrowseId) ?? null;
  }, [displayCollections, collectionBrowseId]);

  const handleViewCollection = (collectionId: string) => {
    setCollectionBrowseId(collectionId);
    setCollectionBrowseOpen(true);
  };

  const handleCollectionCreated = useCallback(
    (collection: ProfileCollectionHighlight) => {
      onCollectionCreated?.(collection);
      setCollectionBrowseId(collection.id);
      setCollectionBrowseOpen(true);
    },
    [onCollectionCreated],
  );

  const handleCollectionDeleted = useCallback(
    async (collectionId: string) => {
      await onCollectionDeleted?.(collectionId);
      if (collectionBrowseId === collectionId) {
        setCollectionBrowseOpen(false);
        setCollectionBrowseId(null);
        setAddItemsOpen(false);
      }
    },
    [collectionBrowseId, onCollectionDeleted],
  );

  const addItemsCandidates = useMemo((): ListingRow[] => {
    if (!collectionBrowseId) return [];
    const collection = displayCollections.find((c) => c.id === collectionBrowseId);
    const inCollection = new Set((collection?.listings ?? []).map((l) => l.id));
    return listings
      .filter((listing) => !inCollection.has(listing.id))
      .map((listing) => ({
        id: listing.id,
        created_at: listing.created_at,
        title: listing.title,
        description: listing.description,
        price: listing.price ? Number(listing.price) : null,
        currency: listing.currency ?? null,
        shipping_config: "",
        // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: free-text `location` no
        // longer flows through the read shape. Add-items-to-collection
        // dialog doesn't render this field; pass empty string to satisfy
        // the legacy `ListingRow` contract until Phase 3 removes the field.
        location: "",
        grade: listing.grade,
        condition: listing.condition,
        quantity: listing.quantity,
        image_urls: listing.image_urls,
        scheduled_publish_at: listing.scheduled_publish_at ?? null,
        listing_format: listing.listing_format ?? null,
        type: listing.type ?? null,
      }));
  }, [collectionBrowseId, displayCollections, listings]);

  const handleAddItemsConfirm = useCallback(
    async (listingIds: string[]) => {
      if (!collectionBrowseId || !onAddItemsToCollection || listingIds.length === 0) return;
      setIsAddingItems(true);
      try {
        const ok = await onAddItemsToCollection(collectionBrowseId, listingIds);
        if (ok) setAddItemsOpen(false);
      } finally {
        setIsAddingItems(false);
      }
    },
    [collectionBrowseId, onAddItemsToCollection],
  );

  const shopListingsCount = totalListingsCount ?? listings.length;

  return (
    <>
      <div className="w-full" ref={resolvedTabsRef as React.RefObject<HTMLDivElement>}>
        {(highlightCollections.length > 0 || (isOwner && !sellerBanned)) && !searchActive ? (
          <ProfileCollectionHighlights
            collections={highlightCollections}
            isOwner={isOwner}
            sellerBanned={sellerBanned}
            onSelectCollection={handleViewCollection}
            onCollectionCreated={handleCollectionCreated}
            onCollectionDeleted={isOwner && !sellerBanned ? handleCollectionDeleted : undefined}
            className="mb-4"
          />
        ) : null}

        {!searchActive ? (
          <ProfileTabBar
            listingsCount={shopListingsCount}
            className="transition-opacity duration-200"
            style={{ opacity: 1 - mobileHeaderTabsOpacity }}
          />
        ) : null}

        <div className="pt-3">
          {listings.length > 0 ? (
            <div className="profile-shop-grid">
              <ListingResults
                listings={listings}
                searchQuery=""
                showHeader={false}
                useInfiniteScroll={true}
                limit={16}
                sellerBanned={sellerBanned}
                currentUserId={currentUserId}
                onEndReached={onLoadMoreListings}
                hasMoreFromServer={hasMoreListings}
                isLoadingMore={isLoadingMoreListings}
                onListingClickAction={(listing) => {
                  router.push(`/listings/${listing.id}`);
                }}
              />
            </div>
          ) : searchActive && (totalListingsCount ?? 0) > 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-base font-semibold text-foreground">{tProfile("searchNoMatchTitle")}</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-[280px]">{tProfile("searchNoMatchDesc")}</p>
            </div>
          ) : highlightCollections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                <PackageOpen className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-semibold text-foreground">
                {isOwner ? t("noListings") : t("noListingsOther")}
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-[280px]">
                {isOwner
                  ? t("noListingsDesc")
                  : t("noListingsDescOther")}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <ProfileCollectionSlider
        open={collectionBrowseOpen}
        onOpenChangeAction={(open) => {
          setCollectionBrowseOpen(open);
          if (!open) {
            setCollectionBrowseId(null);
            setAddItemsOpen(false);
          }
        }}
        collection={
          browseCollection
            ? {
                id: browseCollection.id,
                title: browseCollection.title,
                description: browseCollection.description,
                listings: browseCollection.listings,
              }
            : null
        }
        currentUserId={currentUserId}
        sellerBanned={sellerBanned}
        isOwner={isOwner}
        onCollectionTitleChange={isOwner ? onCollectionTitleChange : undefined}
        onCollectionDescriptionChange={isOwner ? onCollectionDescriptionChange : undefined}
        onListingRemovedFromCollection={isOwner ? onListingRemovedFromCollection : undefined}
        onAddItems={
          isOwner && !sellerBanned && onAddItemsToCollection
            ? () => setAddItemsOpen(true)
            : undefined
        }
      />

      {isOwner && !sellerBanned && onAddItemsToCollection ? (
        <AddItemsToCollectionDialog
          open={addItemsOpen}
          onOpenChange={setAddItemsOpen}
          collectionTitle={browseCollection?.title ?? ""}
          candidates={addItemsCandidates}
          isAdding={isAddingItems}
          onConfirm={(listingIds) => void handleAddItemsConfirm(listingIds)}
        />
      ) : null}
  </>
  );
}
